import {
  Controller,
  useForm,
  type Control,
  type FieldErrors,
  type FieldValues,
  type Resolver,
} from "react-hook-form";
import { z } from "zod";
import { InputText, Modal, Select, type SelectOption } from "@/deps/ui-library";
import { useCreateAccount, useUpdateAccount } from "@/api/queries";
import type { Account, AccountGroup, AccountSubtype } from "@/api/types";
import { CLASS_ORDER, isBankCode, statementLinesFor, subtypesFor } from "@/lib/chart";
import { refusalCode, refusalMessage } from "./refusal";

const GROUPS = ["Assets", "Liabilities", "Equity", "Income", "Expenses"] as const;

/** The type and line have to belong to the class; a class change that strands them clears them. */
const shape = {
  name: z.string().trim().min(1, "Give the account a name."),
  subtype: z.string().min(1, "Choose the account type."),
  statementLine: z.string().min(1, "Choose where it sits on the statements."),
};

function newSchema(taken: ReadonlySet<string>) {
  return z.object({
    code: z
      .string()
      .trim()
      .regex(/^\d{3,5}$/, "A code is 3 to 5 digits.")
      .refine(
        (code) => !isBankCode(code),
        "Codes 1000–1099 are bank and cash accounts. Add those in Banking.",
      )
      .refine((code) => !taken.has(code), "That code is already on the chart."),
    group: z.enum(GROUPS, { message: "Choose the class." }),
    ...shape,
  });
}
const editSchema = z.object(shape);

type NewValues = {
  code: string;
  group: AccountGroup | "";
  name: string;
  subtype: string;
  statementLine: string;
};
type EditValues = { name: string; subtype: string; statementLine: string };

/** Each problem on its own field, as react-hook-form asks for it. */
function resolverFor<V extends FieldValues>(schema: z.ZodType): Resolver<V> {
  return (values) => {
    const parsed = schema.safeParse(values);
    if (parsed.success) return { values: parsed.data as V, errors: {} };
    const errors: Record<string, { type: string; message: string }> = {};
    for (const issue of parsed.error.issues) {
      errors[String(issue.path[0])] ??= { type: issue.code, message: issue.message };
    }
    return { values: {}, errors: errors as FieldErrors<V> };
  };
}

const pick = (details: { value: SelectOption | SelectOption[] | null }) => {
  const one = Array.isArray(details.value) ? details.value[0] : details.value;
  return one ? String(one.value) : "";
};

const subtypeOptions = (group: AccountGroup | "") =>
  group ? subtypesFor(group).map((s) => ({ value: s.value, label: s.label })) : [];
const lineOptions = (group: AccountGroup | "") =>
  group ? statementLinesFor(group).map((l) => ({ value: l.code, label: l.name })) : [];

/** New account (handoff §6.10): any code but the bank range and what is taken, on a line its class takes. */
export function NewAccountDialog({
  chart,
  onClose,
}: {
  chart: readonly Account[];
  onClose: () => void;
}) {
  const create = useCreateAccount();
  const taken = new Set(chart.map((a) => a.code));
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors },
  } = useForm<NewValues>({
    resolver: resolverFor<NewValues>(newSchema(taken)),
    defaultValues: { code: "", group: "", name: "", subtype: "", statementLine: "" },
  });
  const group = watch("group");
  const codeTaken = refusalCode(create.error) === "account_code_in_use";

  return (
    <Modal.Form
      open
      title="New account"
      subtitle="Codes are 3 to 5 digits and the first digit is the class. Bank and cash accounts (1000–1099) are added in Banking."
      submitLabel="Add account"
      busy={create.isPending}
      error={codeTaken ? null : refusalMessage(create.error)}
      onClose={onClose}
      onSubmit={() =>
        void handleSubmit((values) =>
          create.mutate(
            {
              code: values.code.trim(),
              name: values.name.trim(),
              group: values.group as AccountGroup,
              subtype: values.subtype as AccountSubtype,
              statementLine: values.statementLine,
            },
            {
              onSuccess: onClose,
              onError: (error) => {
                if (refusalCode(error) === "account_code_in_use") {
                  setError("code", { message: (error as Error).message });
                }
              },
            },
          ),
        )()
      }
    >
      <Controller
        control={control}
        name="code"
        render={({ field }) => (
          <InputText
            required
            label="Code"
            inputMode="numeric"
            placeholder="6150"
            value={field.value}
            errorText={errors.code?.message}
            onChange={(event) => field.onChange(event.target.value)}
          />
        )}
      />
      <Controller
        control={control}
        name="name"
        render={({ field }) => (
          <InputText
            required
            label="Name"
            value={field.value}
            errorText={errors.name?.message}
            onChange={(event) => field.onChange(event.target.value)}
          />
        )}
      />
      <Controller
        control={control}
        name="group"
        render={({ field }) => (
          <Select
            required
            label="Class"
            placeholder="Choose the class"
            options={CLASS_ORDER.map((g) => ({ value: g, label: g }))}
            value={field.value}
            errorText={errors.group?.message}
            onChange={(details) => {
              const next = pick(details) as AccountGroup;
              field.onChange(next);
              // A type or line from another class would be refused; start them again.
              setValue("subtype", "");
              setValue("statementLine", "");
            }}
          />
        )}
      />
      <TypeAndLine
        control={control as unknown as Control<EditValues>}
        group={group}
        errors={errors}
      />
    </Modal.Form>
  );
}

/** Edit account: name, type and statement line. The code and the class stay; a required account never opens this. */
export function EditAccountDialog({ account, onClose }: { account: Account; onClose: () => void }) {
  const update = useUpdateAccount();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<EditValues>({
    resolver: resolverFor<EditValues>(editSchema),
    defaultValues: {
      name: account.name,
      subtype: account.subtype ?? "",
      statementLine: account.statementLine ?? "",
    },
  });

  return (
    <Modal.Form
      open
      title={`Edit ${account.code} · ${account.name}`}
      subtitle={`${account.group}. The code, the class and everything posted to it stay the same.`}
      submitLabel="Save"
      busy={update.isPending}
      error={refusalMessage(update.error)}
      onClose={onClose}
      onSubmit={() =>
        void handleSubmit((values) =>
          update.mutate(
            {
              id: account.id,
              edit: {
                name: values.name.trim(),
                subtype: values.subtype as AccountSubtype,
                statementLine: values.statementLine,
              },
            },
            { onSuccess: onClose },
          ),
        )()
      }
    >
      <Controller
        control={control}
        name="name"
        render={({ field }) => (
          <InputText
            required
            label="Name"
            value={field.value}
            errorText={errors.name?.message}
            onChange={(event) => field.onChange(event.target.value)}
          />
        )}
      />
      <TypeAndLine control={control} group={account.group} errors={errors} />
    </Modal.Form>
  );
}

/** The type (subtypes of the class) and the statement line (lines the class takes, handoff §8). */
function TypeAndLine({
  control,
  group,
  errors,
}: {
  // Both forms carry these two fields under the same names.
  control: Control<EditValues>;
  group: AccountGroup | "";
  errors: Partial<Record<"subtype" | "statementLine", { message?: string }>>;
}) {
  const noClass = group === "" ? "Choose the class first." : undefined;
  return (
    <>
      <Controller
        control={control}
        name="subtype"
        render={({ field }) => (
          <Select
            required
            label="Type"
            placeholder="Choose the type"
            disabled={group === ""}
            options={subtypeOptions(group)}
            value={field.value}
            helperText={noClass}
            errorText={errors.subtype?.message}
            onChange={(details) => field.onChange(pick(details))}
          />
        )}
      />
      <Controller
        control={control}
        name="statementLine"
        render={({ field }) => (
          <Select
            required
            searchable
            label="Statement line"
            placeholder="Choose the line"
            disabled={group === ""}
            options={lineOptions(group)}
            value={field.value}
            helperText={
              noClass ??
              "Where it sits on the statement of financial position or the income statement. Current or non-current comes from the type."
            }
            errorText={errors.statementLine?.message}
            onChange={(details) => field.onChange(pick(details))}
          />
        )}
      />
    </>
  );
}
